import { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  api,
  type BatchItemPayload,
  type BatchResult,
  type CheckOutcome,
  type Direction,
} from "./api";

/// Um pacote conferido, ainda NÃO gravado.
///
/// O lote vive na tela até o operador apertar Entregar. Cada item carrega o
/// próprio motivo de devolução — e não um motivo do lote — porque trocar de
/// motivo no meio da conferência não pode reescrever o que já foi bipado.
export type BatchItem = {
  /// Chave local. É o código normalizado, que já é único por pacote.
  key: string;
  raw: string;
  check: CheckOutcome;
  direction: Direction;
  /// Conta escolhida pelo operador quando a etiqueta não resolveu sozinha.
  accountId: string | null;
  accountName: string | null;
  /// Transportadora trocada à mão.
  carrierId: string | null;
  carrierName: string | null;
  reason: string | null;
  /// Foto tirada para esta devolução, ainda no aparelho. Sobe depois do
  /// Entregar, quando o pacote já existe no banco para receber o anexo.
  photoDataUrls: string[];
  /// Observação livre do operador sobre ESTE pacote.
  notes: string;
  /// Custo da devolução, em centavos. Avaria ou frete de retorno.
  returnCostCents: number | null;
  at: number;
};

export type AddResult =
  | { kind: "added"; item: BatchItem }
  | { kind: "duplicate-batch"; item: BatchItem }
  | { kind: "already-delivered"; check: CheckOutcome }
  | { kind: "unknown"; check: CheckOutcome };

type Ctx = {
  items: BatchItem[];
  add: (
    raw: string,
    direction: Direction,
    reason: string | null,
    photoDataUrls?: string[],
    returnCostCents?: number | null
  ) => Promise<AddResult>;
  remove: (key: string) => void;
  setAccount: (key: string, accountId: string, accountName: string) => void;
  setCarrier: (key: string, carrierId: string, carrierName: string) => void;
  setNotes: (key: string, notes: string) => void;
  setReturnCost: (key: string, centavos: number | null) => void;
  clear: () => void;
  deliver: () => Promise<BatchResult & { fotosEnviadas: number; fotosFalharam: number }>;
};

const BatchContext = createContext<Ctx | null>(null);

export function BatchProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<BatchItem[]>([]);

  const add = useCallback(
    async (
      raw: string,
      direction: Direction,
      reason: string | null,
      photoDataUrls: string[] = [],
      custoInicial: number | null = null
    ): Promise<AddResult> => {
      const check = await api.check(raw, direction);

      // Já está no lote atual: nada muda, e o operador ouve o aviso.
      const noLote = items.find((i) => i.key === check.code);
      if (noLote) return { kind: "duplicate-batch", item: noLote };

      // Já foi entregue antes, em outra sessão ou por outro operador. Só avisa
      // — não entra no lote e não vira leitura repetida no histórico.
      if (check.alreadyScanned) return { kind: "already-delivered", check };

      const item: BatchItem = {
        key: check.code,
        raw,
        check,
        direction,
        accountId: check.account?.id ?? null,
        accountName: check.account?.name ?? null,
        carrierId: check.carrier?.id ?? null,
        carrierName: check.carrier?.name ?? null,
        // Congelado no momento da leitura: é isto que impede trocar o motivo
        // depois e alterar o que já foi bipado.
        reason,
        // Congelada junto com o motivo: trocar a foto depois não pode
        // reescrever a prova do pacote já bipado.
        photoDataUrls,
        notes: "",
        // Congelado como o motivo e a foto: trocar o valor na tela depois não
        // pode reescrever o que já foi bipado.
        returnCostCents: custoInicial,
        at: Date.now(),
      };

      setItems((prev) => [item, ...prev]);

      if (check.labelFormat === null && check.account === null) {
        return { kind: "unknown", check };
      }

      return { kind: "added", item };
    },
    [items]
  );

  const remove = useCallback((key: string) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const patch = useCallback((key: string, data: Partial<BatchItem>) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...data } : i)));
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      items,
      add,
      remove,
      setAccount: (key, accountId, accountName) => patch(key, { accountId, accountName }),
      setCarrier: (key, carrierId, carrierName) => patch(key, { carrierId, carrierName }),
      setNotes: (key, notes) => patch(key, { notes }),
      setReturnCost: (key, returnCostCents) => patch(key, { returnCostCents }),
      clear: () => setItems([]),

      deliver: async () => {
        const payload: BatchItemPayload[] = items.map((i) => ({
          code: i.raw,
          direction: i.direction,
          ...(i.accountId ? { accountId: i.accountId } : {}),
          ...(i.carrierId ? { carrierId: i.carrierId } : {}),
          ...(i.reason ? { reason: i.reason } : {}),
          ...(i.notes.trim() ? { notes: i.notes.trim() } : {}),
          ...(i.returnCostCents !== null ? { returnCostCents: i.returnCostCents } : {}),
        }));

        const result = await api.deliver(payload);

        // As fotos sobem DEPOIS do lote: elas se anexam ao pacote, que só
        // passa a existir no banco agora. Falha de foto não desfaz a bipagem —
        // o pacote registrado vale mais que a imagem, e o operador é avisado
        // do que faltou.
        const porCodigo = new Map(result.registrados.map((r) => [r.code, r.packageId]));
        let fotosEnviadas = 0;
        let fotosFalharam = 0;

        for (const item of items) {
          if (item.direction !== "RETURN" || item.photoDataUrls.length === 0) continue;

          const packageId = porCodigo.get(item.key);
          if (!packageId) continue;

          // Uma de cada vez, e falha de uma não interrompe as outras: cada
          // ângulo é uma prova independente, e perder todas porque a terceira
          // falhou seria pior do que subir as duas que deram certo.
          for (const dataUrl of item.photoDataUrls) {
            try {
              await api.uploadReturnPhoto(packageId, dataUrl);
              fotosEnviadas++;
            } catch {
              fotosFalharam++;
            }
          }
        }

        // Só some da tela o que o servidor confirmou. O que falhou fica para o
        // operador ver e tentar de novo — perder a conferência silenciosamente
        // seria pior do que mostrar o erro.
        const gravados = new Set(result.registrados.map((r) => r.code));
        setItems((prev) => prev.filter((i) => !gravados.has(i.key)));

        return { ...result, fotosEnviadas, fotosFalharam };
      },
    }),
    [items, add, remove, patch]
  );

  return <BatchContext.Provider value={value}>{children}</BatchContext.Provider>;
}

export function useBatch(): Ctx {
  const ctx = useContext(BatchContext);
  if (!ctx) throw new Error("useBatch precisa estar dentro de <BatchProvider>.");
  return ctx;
}
